import React from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNowStrict } from 'date-fns';
import { X } from 'lucide-react';

/**
 * Reusable notification card: header bar, body, and a "from" field that plays the
 * role the "powered by ShowReady" line plays in outbound email, but names the actual
 * source instead (a feature, an integration, or the admin team for a broadcast).
 * Used by both the header drawer and the full "All Notifications" page.
 *
 * `onDismiss` is optional - when passed, a small X in the top-right corner deletes the
 * notification. It's a sibling of the clickable card, not a child, since a button can't
 * be nested inside the card's own <Link>/<button>.
 */
const NotificationItem = ({ notification, onOpen, onDismiss }) => {
    const isUnread = !notification.read_at;
    const timeAgo = formatDistanceToNowStrict(new Date(notification.created_at), { addSuffix: true });

    const body = (
        <div className={`flex flex-col gap-1.5 px-4 py-3 ${onDismiss ? 'pr-9' : ''}`}>
            <div className="flex items-start gap-2">
                <span className={`mt-1.5 h-1.5 w-1.5 rounded-full flex-shrink-0 ${isUnread ? 'bg-amber-400' : 'bg-transparent'}`} />
                <div className="flex-grow flex items-baseline justify-between gap-2 min-w-0">
                    <span className={`text-sm truncate ${isUnread ? 'font-bold text-white' : 'font-semibold text-gray-400'}`}>
                        {notification.header}
                    </span>
                    <span className="text-xs text-gray-500 flex-shrink-0">{timeAgo}</span>
                </div>
            </div>
            <p className={`text-sm leading-snug pl-3.5 line-clamp-2 ${isUnread ? 'text-gray-300' : 'text-gray-500'}`}>
                {notification.body}
            </p>
            <p className={`text-xs italic pl-3.5 ${isUnread ? 'text-gray-500' : 'text-gray-600'}`}>
                {notification.from_label}
            </p>
        </div>
    );

    const cardClass = `block w-full text-left transition-colors ${isUnread ? 'bg-gray-700/30 hover:bg-gray-700/50' : 'hover:bg-gray-700/30'}`;
    const handleClick = () => onOpen && onOpen(notification);

    const card = notification.link_url ? (
        <Link to={notification.link_url} onClick={handleClick} className={cardClass}>
            {body}
        </Link>
    ) : (
        <button type="button" onClick={handleClick} className={cardClass}>
            {body}
        </button>
    );

    return (
        <div className="relative border-b border-gray-700/60 last:border-0">
            {card}
            {onDismiss && (
                <button
                    type="button"
                    onClick={() => onDismiss(notification)}
                    className="absolute top-2 right-2 p-1 rounded text-gray-500 hover:text-white hover:bg-gray-600 transition-colors"
                    aria-label="Dismiss notification"
                    title="Dismiss"
                >
                    <X size={14} />
                </button>
            )}
        </div>
    );
};

export default NotificationItem;
